// One place's page (CR_Manager_vs_User_role §5.2): a warehouse at /warehouses/:siteId, or a project location at
// /locations/:siteId, with tabs Overview · Assets · Map · Stock counts · Transfers (?tab=). The header carries the
// place's actions: open / start its stock count, zones and edit (managers), Transfers, receive what arrived. The Map
// tab reads out the place (geofence, timers, count) beside a compact live map (PlaceLocations).
import { Alert, Box, Button, Chip, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import { type PlaceKind, placeKind } from '@fleetspoke/core/places';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { canManage, useSession } from '../auth/session';
import { NewTransferButton } from '../components/NewTransferDialog';
import { Loading, LoadError, Page } from '../components/Page';
import { PlaceAssets, ReceiveDialog } from '../components/place/PlaceAssets';
import { PlaceCounts } from '../components/place/PlaceCounts';
import { PlaceMovements } from '../components/place/PlaceMovements';
import { PlaceSummary } from '../components/place/PlaceSummary';
import { placeTypeLabel } from '../components/place/SiteCard';
import { ZonesDialog } from '../components/ZonesDialog';
import { keys, useSiteBoard, useSites } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import { useCountStatus } from '../data/workflow';
import { activeZones, useZones } from '../data/zones';
import { withParam } from '../lib/assetFilters';
import { PLACE_TABS, type PlaceTab, placePath, placeTab } from '../lib/places';
import { NewCountDialog } from './CountsPage';
import { ToPrepareCard } from '../components/requests';

// MapLibre (+ Draw) load only when the Map tab or the edit dialog opens
const PlaceLocations = lazy(() =>
  import('../components/place/PlaceLocations').then((m) => ({ default: m.PlaceLocations })),
);
const SiteDialogLoader = lazy(() =>
  import('../components/SiteDialog').then((m) => ({ default: m.SiteDialogLoader })),
);

export function PlacePage({ kind }: { kind: PlaceKind }) {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const sites = useSites();
  const board = useSiteBoard(siteId);
  const zones = useZones(siteId);
  const countStatus = useCountStatus(siteId, !!siteId);
  const [receiving, setReceiving] = useState(false);
  const [managingZones, setManagingZones] = useState(false);
  const [startingCount, setStartingCount] = useState(false);
  useLiveInvalidate(
    `place-${siteId}`,
    ['asset_presence', 'transfers', 'transfer_lines', 'gateways', 'asset_zone_moves'],
    [keys.board(siteId), keys.sites, ['history'], ['incoming', siteId], ['zone-moves']],
  );

  const site = sites.data?.find((s) => s.site_id === siteId);
  const tab = placeTab(params.get('tab'));
  // ?edit=1 (old Settings → Sites links, a card's pencil) opens the edit dialog
  const editing = params.get('edit') === '1';
  const setEditing = (on: boolean) =>
    setParams(withParam(params, 'edit', on ? '1' : ''), { replace: true });

  if (sites.isLoading) return <Loading />;
  if (sites.error || !site)
    return (
      <Page title={t(kind === 'warehouse' ? 'nav.warehouses' : 'nav.projects')}>
        <LoadError error={sites.error ?? new Error(t('places.not_found'))} />
      </Page>
    );
  // a warehouse id under /locations (or the other way round), or a location of a project, goes where it belongs
  if (placeKind(site.type) !== kind || (kind === 'location' && site.project_id))
    return <Navigate to={placePath(site, tab, params)} replace />;

  const manager = canManage(me);
  const count = countStatus.data?.[0];
  const active = activeZones(zones.data);
  const back =
    kind === 'warehouse' ? (
      <Link component={RouterLink} to="/warehouses" variant="body2">
        ← {t('nav.warehouses')}
      </Link>
    ) : (
      <Link component={RouterLink} to="/projects" variant="body2">
        ← {t('nav.projects')}
      </Link>
    );
  const setTab = (value: PlaceTab) =>
    setParams(withParam(params, 'tab', value === 'overview' ? '' : value), { replace: true });

  return (
    <Page
      back={back}
      title={
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{site.name}</span>
          <Typography component="span" color="text.secondary">
            {placeTypeLabel(site.type, t)}
          </Typography>
          {site.is_active ? null : <Chip size="small" label={t('sites.inactive')} />}
        </Stack>
      }
      actions={
        <>
          {/* an open count is reached from the Stock counts tab, which says the same thing in context
              (owner, 2026-09-18): in the header it was a fourth status competing with the page's own figures */}
          {!count?.open_count && count?.can_count ? (
            <Button
              variant="outlined"
              onClick={() => setStartingCount(true)}
              data-testid="start-count"
            >
              {t('counts.start')}
            </Button>
          ) : null}
          {manager ? (
            <Button variant="outlined" onClick={() => setManagingZones(true)}>
              {active.length ? `${t('zones.button')} (${active.length})` : t('zones.button')}
            </Button>
          ) : null}
          {manager ? (
            <Button variant="outlined" onClick={() => setEditing(true)} data-testid="edit-place">
              {t('common.edit')}
            </Button>
          ) : null}
          {/* New transfer from this place ("Transfers", owner 2026-09-15) */}
          <NewTransferButton fromIds={[site.site_id]} initialFrom={site.site_id} />
          <Button
            variant="contained"
            disabled={site.awaiting_receipt === 0}
            onClick={() => setReceiving(true)}
            data-testid="receive"
          >
            {t('board.receive', { count: site.awaiting_receipt })}
          </Button>
        </>
      }
    >
      {kind === 'location' && !site.project_id ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('sites.no_project')}
        </Alert>
      ) : null}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v: PlaceTab) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {PLACE_TABS.map((value) => (
            <Tab
              key={value}
              value={value}
              label={t(`places.tab_${value}`)}
              data-testid={`tab-${value}`}
            />
          ))}
        </Tabs>
      </Box>
      {tab === 'overview' && kind === 'warehouse' ? <ToPrepareCard siteId={site.site_id} /> : null}
      {tab === 'overview' ? (
        <PlaceSummary
          site={site}
          kind={kind}
          count={count}
          zones={active.length}
          onOpenAssets={() => setTab('assets')}
          onOpenCounts={() => setTab('counts')}
          onOpenZones={manager ? () => setManagingZones(true) : undefined}
        />
      ) : null}
      {tab === 'assets' ? <PlaceAssets site={site} /> : null}
      {tab === 'map' ? (
        <Suspense fallback={<Loading />}>
          <PlaceLocations
            sites={[site]}
            counts={count ? [count] : []}
            manager={manager}
            onAssets={() => setTab('assets')}
            onEdit={() => setEditing(true)}
          />
        </Suspense>
      ) : null}
      {tab === 'counts' ? <PlaceCounts siteIds={[site.site_id]} /> : null}
      {tab === 'movements' ? <PlaceMovements siteIds={[site.site_id]} /> : null}

      {receiving ? (
        <ReceiveDialog
          siteId={siteId}
          board={board.data ?? []}
          zones={active}
          open={receiving}
          onClose={() => setReceiving(false)}
        />
      ) : null}
      {startingCount ? (
        <NewCountDialog siteId={siteId} onClose={() => setStartingCount(false)} />
      ) : null}
      {managingZones && me?.company ? (
        <ZonesDialog
          siteId={siteId}
          siteName={site.name}
          companyId={site.company_id}
          onClose={() => setManagingZones(false)}
        />
      ) : null}
      {editing && manager ? (
        <Suspense fallback={<Loading />}>
          <SiteDialogLoader kind={kind} siteId={siteId} onClose={() => setEditing(false)} />
        </Suspense>
      ) : null}
    </Page>
  );
}
